import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "../lib/api";

export default function ImageLightbox({ src, images, captions, startIndex = 0, alt = "", onClose }) {
  const list = images && images.length ? images : (src ? [src] : []);
  const caps = captions || [];
  const [idx, setIdx] = useState(startIndex);
  useEffect(() => { setIdx(startIndex); }, [startIndex, src]);

  const prev = useCallback((e) => { e?.stopPropagation?.(); setIdx((i) => (i - 1 + list.length) % list.length); }, [list.length]);
  const next = useCallback((e) => { e?.stopPropagation?.(); setIdx((i) => (i + 1) % list.length); }, [list.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  if (!list.length) return null;
  const many = list.length > 1;

  let touchX = null;
  const onTouchStart = (e) => { touchX = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX === null || !many) return;
    const dx = e.changedTouches[0].clientX - touchX;
    if (dx > 50) prev(); else if (dx < -50) next();
    touchX = null;
  };

  return (
    <div data-testid="image-lightbox" onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150">
      <button data-testid="lightbox-close" onClick={onClose}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 z-10">
        <X className="w-5 h-5" />
      </button>

      {many && (
        <button data-testid="lightbox-prev" onClick={prev}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 z-10">
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      <img src={fileUrl(list[idx])} alt={alt} onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-[88vh] w-auto h-auto object-contain rounded-2xl shadow-2xl" />

      {caps[idx] && (
        <div data-testid="lightbox-caption" onClick={(e) => e.stopPropagation()}
          className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-[85vw] text-center bg-black/55 text-white text-sm px-4 py-2 rounded-2xl backdrop-blur">
          {caps[idx]}
        </div>
      )}

      {many && (
        <button data-testid="lightbox-next" onClick={next}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 z-10">
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {many && (
        <div data-testid="lightbox-counter" onClick={(e) => e.stopPropagation()}
          className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-white/15 text-white text-sm font-mono px-3 py-1 rounded-full">
          {idx + 1} / {list.length}
        </div>
      )}
    </div>
  );
}
